import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { ThemeProvider } from './contexts/ThemeContext';
import { installClientVersionWatch, runClientVersionGuard } from './utils/clientVersionGuard';
import { installFrontendDebugSinkReporter, reportFrontendDebugSinkError } from './utils/debugSinkReporter';
import { checkServerAvailability } from './utils/serverAvailability';
import './styles/tokens.css';
import './styles/recipes.css';
import './styles/antd.css';
import './styles/index.css';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    void reportFrontendDebugSinkError('react.error_boundary', error, {
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div
          style={{
            padding: 24,
            background: 'var(--mg-page-bg, #141619)',
            color: 'var(--mg-text-primary, #e8eaed)',
            minHeight: '100vh',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1 style={{ color: 'var(--mg-tone-danger, #ff7875)' }}>Ошибка загрузки</h1>
          <pre style={{ overflow: 'auto', fontSize: 12 }}>
            {this.state.error.message}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}
          >
            Обновить страницу
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const appRoot = ReactDOM.createRoot(document.getElementById('root')!);

const mountApp = () => {
  appRoot.render(
    <React.StrictMode>
      <ErrorBoundary>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
};

const renderServerUpdateScreen = () => {
  appRoot.render(
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: 'var(--mg-page-bg, #0b1424)',
        color: 'var(--mg-text-primary, #f3f6fb)',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div style={{ maxWidth: 520, textAlign: 'center' }}>
        <div style={{ fontSize: 42, lineHeight: 1, marginBottom: 20 }}>↻</div>
        <h1 style={{ margin: '0 0 12px', fontSize: 26 }}>Идёт обновление сервера</h1>
        <p style={{ margin: 0, color: 'var(--mg-text-secondary, #aeb8c7)', lineHeight: 1.55 }}>
          Пожалуйста, подождите несколько минут. Страница продолжит работу автоматически после завершения обновления.
        </p>
      </div>
    </div>,
  );
};

const waitForServerRecovery = async (): Promise<void> => {
  while (!(await checkServerAvailability())) {
    await new Promise<void>((resolve) => window.setTimeout(resolve, 3_000));
  }
};

installFrontendDebugSinkReporter();

void (async () => {
  const serverAvailable = await checkServerAvailability();
  if (!serverAvailable) {
    renderServerUpdateScreen();
    await waitForServerRecovery();
    window.location.reload();
    return;
  }
  try {
    const result = await runClientVersionGuard();
    if (result.reloaded) return;
  } catch {
    // A version-check failure is non-fatal when the health endpoint is ready.
  }
  mountApp();
  installClientVersionWatch();
})();
